import { useCallback, useEffect, useRef, useState } from 'react'
import type { ApiFailure, ApiResult } from './client'

/**
 * The typed query and mutation layer.
 *
 * Hand-written rather than TanStack Query, and that is a size judgement rather
 * than a preference: the whole of what the shell needs is "run this, tell me
 * loading / ready / failed, and let me run it again". Caching, background
 * refetching and query invalidation are answers to problems this app does not
 * have yet — every screen here reads a handful of rows behind an authenticated
 * cookie — and a cache is the thing that turns a stale-data bug into a support
 * ticket nobody can reproduce. When a screen genuinely needs cross-component
 * cache coherence, that is the session to reach for a library in.
 *
 * Both hooks are built on `call()` from `./client`, so both share the one error
 * envelope, the one network-failure fallback and the one session-expiry signal.
 */

export type QueryState<T> =
  | { status: 'loading'; data: null; failure: null }
  | { status: 'ready'; data: T; failure: null }
  | { status: 'error'; data: null; failure: ApiFailure }

export interface Query<T> {
  state: QueryState<T>
  /** Run it again — after a mutation, or from the error state's retry button. */
  refetch: () => void
}

const LOADING = { status: 'loading', data: null, failure: null } as const

/**
 * Read something from the API for as long as this component is mounted.
 *
 * `run` receives an `AbortSignal` and must pass it to the generated call. That
 * is not tidiness: unmounting should *stop* the request, not merely ignore its
 * answer, and it is the only version of this a type checker can reason about —
 * a captured `cancelled` boolean is provably false at every point it is read,
 * so the guard reads as dead code.
 *
 * `run` is held in a ref and re-read on each attempt, so a caller may pass an
 * inline closure without the query restarting on every render. What decides
 * *when* it re-runs is `deps`, exactly like `useEffect` — and `refetch`, which
 * bumps a counter rather than calling `run` directly so that a second refetch
 * during an in-flight one cannot leave two responses racing for the same state.
 */
export function useQuery<T>(
  run: (signal: AbortSignal) => Promise<ApiResult<T>>,
  deps: readonly unknown[],
): Query<T> {
  const [state, setState] = useState<QueryState<T>>(LOADING)
  const [attempt, setAttempt] = useState(0)

  const latest = useRef(run)
  latest.current = run

  useEffect(() => {
    const controller = new AbortController()
    setState(LOADING)

    void (async () => {
      try {
        const result = await latest.current(controller.signal)
        if (controller.signal.aborted) return
        setState(
          result.ok
            ? { status: 'ready', data: result.data, failure: null }
            : { status: 'error', data: null, failure: result.failure },
        )
      } catch {
        // `call()` re-throws only `AbortError`, and an aborted request belongs
        // to a component that has already gone.
      }
    })()

    return () => {
      controller.abort()
    }
    // `run` is deliberately absent — see the ref above. The caller states its
    // real inputs in `deps`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, ...deps])

  const refetch = useCallback(() => {
    setAttempt((n) => n + 1)
  }, [])

  return { state, refetch }
}

export type MutationState =
  | { status: 'idle'; failure: null }
  | { status: 'running'; failure: null }
  | { status: 'done'; failure: null }
  | { status: 'error'; failure: ApiFailure }

export interface Mutation<V, T> {
  /**
   * Run it. Resolves with the result rather than only setting state, so a
   * caller can navigate, toast or read the response body on success without
   * waiting for a re-render to tell it what happened.
   */
  run: (value: V) => Promise<ApiResult<T>>
  state: MutationState
  /** Clear a failure — what a form calls when the user edits the field. */
  reset: () => void
  pending: boolean
}

/**
 * Write something to the API.
 *
 * No abort signal: a submitted form must not be cancelled by the component
 * unmounting, because the write has already reached the server and cancelling
 * the *response* would leave the user unsure whether it happened. What unmount
 * does suppress is the state update, via the mounted ref — React logs a warning
 * for a setState after unmount and, more to the point, the state belongs to a
 * component that is gone.
 */
export function useMutation<V, T>(run: (value: V) => Promise<ApiResult<T>>): Mutation<V, T> {
  const [state, setState] = useState<MutationState>({ status: 'idle', failure: null })

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const latest = useRef(run)
  latest.current = run

  const start = useCallback(async (value: V): Promise<ApiResult<T>> => {
    if (mounted.current) setState({ status: 'running', failure: null })
    const result = await latest.current(value)
    if (mounted.current) {
      setState(
        result.ok
          ? { status: 'done', failure: null }
          : { status: 'error', failure: result.failure },
      )
    }
    return result
  }, [])

  const reset = useCallback(() => {
    if (mounted.current) setState({ status: 'idle', failure: null })
  }, [])

  return { run: start, state, reset, pending: state.status === 'running' }
}
